import * as React from 'react'
import Box from '@mui/material/Box'
import { getSXStyles } from './styles'
import { Form, Formik } from 'formik'
import { MainForm } from './MainForm/MainForm'
import { useData, useHandlers, useNoteCodes } from './hooks'
import { useCreateOppActions } from '@user/components/Location'
import { LoadingSpinner, SettingsPage } from '@sales-center/components'
import AddLeadHeader from './AddLeadHeader/AddLeadHeader'
import useMediaQuery from '@mui/material/useMediaQuery'
import { RightRail } from './RightRail/RightRail'
import { TitleSection } from './FormComponents/TitleSection/TitleSection'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle/store'
import { useTheme } from '@mui/material/styles'
import { useRightContainer } from './MainForm/hooks/useRightContainer'
import { DuplicateAlertNew } from '@user/components/Leads/AddLeadPage/FormComponents/DuplicateAlert/DuplicateAlertNew'
import Stack from '@mui/material/Stack'
import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { EntityLocationDTO } from '@user/store/map/typings'

type Props = {
  fromMapView?: boolean
  onClose?: () => void
  handleDuplicateManageClick?: () => void
  handleMapAfterAddLeadSaveSuccess?: (newPin: EntityLocationDTO) => void
  selectedMapLocation?: HereMapClickEvent | null
  draftId?: string | null
}

export function AddLeadPage(props: Props) {
  const {
    fromMapView = false,
    onClose,
    selectedMapLocation,
    handleMapAfterAddLeadSaveSuccess,
    handleDuplicateManageClick,
    draftId,
  } = props
  const ref = React.createRef<HTMLDivElement>()
  const rightRailRef = React.useRef<HTMLDivElement | null>(null)

  const { noteCodes } = useNoteCodes()
  const {
    activeLeadSources,
    activeLeadCampaigns,
    activeOtherSection,
    initialValues,
    createOppActions,
    customFieldSettings,
    customFields,
    disabled,
    draftLead,
    entitySettingsMap,
    isAddFromLocation,
    isFromPestPacPin,
    isDraftLead,
    isMappedWithUpdateLocationPerm,
    loading,
    requiredEntitySettings,
    hideOptional,
    setHideOptional,
    activeAddToPestPac,
  } = useData({ noteCodes, selectedMapLocation, fromMapView, draftId })

  const {
    onCancel,
    onSubmit,
    validate,
    onChangeHideOptional,
    prepareSubmitValidation,
  } = useHandlers({
    isDraftLead,
    setHideOptional,
    disabled,
    customFields,
    entitySettingsMap,
    customFieldSettings,
    requiredEntitySettings,
    isMappedWithUpdateLocationPerm,
    isAddFromLocation,
    fromMapView,
    handleMapAfterAddLeadSaveSuccess,
  })

  const isMobile = useMediaQuery('(max-width:900px)') && !fromMapView
  const tidalThemeActive = useTidalThemeActive()
  const theme = useTheme()
  const styles = getSXStyles(isMobile)

  const mapViewCreateOppActions = useCreateOppActions(
    true,
    handleDuplicateManageClick,
  )

  const { rightContainerHeight } = useRightContainer({
    isMobile,
    activeOtherSection,
    rightRailRef,
  })

  return loading ? (
    <LoadingSpinner />
  ) : (
    <Formik
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      validateOnChange={true}
      validateOnBlur={false}
    >
      <Form
        style={
          fromMapView
            ? {
                backgroundColor: theme.palette.common.white,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }
            : {
                backgroundColor: theme.palette.grey[50],
              }
        }
      >
        {!fromMapView ? (
          <SettingsPage
            padding="0"
            style={{ backgroundColor: theme.palette.grey[50] }}
            hideTitleOnScroll={true}
            footContent={
              isMobile && (
                <DuplicateAlertNew createOppActions={createOppActions} />
              )
            }
            fixedFooter
            headContent={
              <AddLeadHeader
                backgroundColor={
                  isMobile ? theme.palette.grey[50] : theme.palette.common.white
                }
                onCancel={onCancel}
                hideOptional={hideOptional}
                onChangeHideOptional={onChangeHideOptional}
                createOppActions={createOppActions}
                type={draftLead?.type ?? ''}
                isDraftLead={isDraftLead}
                draftLeadId={draftLead?.id}
                draftLead={draftLead}
                isFromLocation={Boolean(isAddFromLocation)}
                isMappedWithUpdateLocationPerm={isMappedWithUpdateLocationPerm}
                noteCodes={noteCodes}
                entitySettingsMap={entitySettingsMap}
                setHideOptional={setHideOptional}
                activeAddToPestPac={activeAddToPestPac}
                prepareSubmitValidation={prepareSubmitValidation}
              />
            }
            contentId="leadForm"
            ref={ref}
          >
            <Box sx={styles.centered}>
              <Box sx={styles.container}>
                <Box sx={styles.title}>
                  <TitleSection
                    hideOptional={hideOptional}
                    activeAddToPestPac={activeAddToPestPac}
                  />
                </Box>

                <Box sx={styles.main}>
                  <Box sx={styles.scroll}>
                    <MainForm
                      innerRef={ref}
                      disabled={disabled}
                      isMappedWithUpdateLocationPerm={
                        isMappedWithUpdateLocationPerm
                      }
                      isDraftLead={isDraftLead}
                      createOppActions={createOppActions}
                      addFromLocation={isAddFromLocation}
                      draftLead={draftLead}
                      activeLeadSources={activeLeadSources}
                      activeLeadCampaigns={activeLeadCampaigns}
                      hideOptional={hideOptional}
                      textFieldProps={{
                        size: tidalThemeActive ? 'medium' : 'small',
                      }}
                    />
                  </Box>

                  {!isMobile && activeOtherSection && (
                    <Box
                      sx={{
                        ...styles.sticky,
                        height: isMobile ? 'auto' : rightContainerHeight,
                      }}
                      ref={rightRailRef}
                    >
                      <RightRail
                        hideOptional={hideOptional}
                        noteCodes={noteCodes}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </SettingsPage>
        ) : (
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <AddLeadHeader
                backgroundColor={theme.palette.common.white}
                onCancel={onCancel}
                hideOptional={hideOptional}
                onChangeHideOptional={onChangeHideOptional}
                createOppActions={createOppActions}
                type={draftLead?.type ?? ''}
                isDraftLead={isDraftLead}
                draftLeadId={draftLead?.id}
                draftLead={draftLead}
                isFromLocation={Boolean(isAddFromLocation) || isFromPestPacPin}
                isMappedWithUpdateLocationPerm={isMappedWithUpdateLocationPerm}
                noteCodes={noteCodes}
                entitySettingsMap={entitySettingsMap}
                setHideOptional={setHideOptional}
                activeAddToPestPac={activeAddToPestPac}
                fromMapView
                onClose={onClose}
                prepareSubmitValidation={prepareSubmitValidation}
              />

              <Stack sx={{ p: 1.5, pt: 0, gap: 3 }}>
                <RightRail
                  hideOptional={hideOptional}
                  noteCodes={noteCodes}
                  fromMapView
                  startFromNotes
                />

                <MainForm
                  fromMapView
                  noPaper
                  innerRef={ref}
                  disabled={disabled}
                  isMappedWithUpdateLocationPerm={
                    isMappedWithUpdateLocationPerm
                  }
                  isDraftLead={isDraftLead}
                  createOppActions={createOppActions}
                  addFromLocation={isAddFromLocation}
                  draftLead={draftLead}
                  activeLeadSources={activeLeadSources}
                  activeLeadCampaigns={activeLeadCampaigns}
                  hideOptional={hideOptional}
                  textFieldProps={{
                    size: tidalThemeActive ? 'medium' : 'small',
                  }}
                />
              </Stack>
            </Stack>

            <Box sx={{ flexShrink: 0 }}>
              <DuplicateAlertNew
                createOppActions={mapViewCreateOppActions}
                handleDuplicateManageClick={handleDuplicateManageClick}
              />
            </Box>
          </Stack>
        )}
      </Form>
    </Formik>
  )
}
